import * as React from 'react';
import Typography from '@mui/material/Typography';
import WidgetCard from 'components/Widget/WidgetCard';
import WidgetTitle from 'components/Widget/WidgetTitle';
import ExportMenuContainer from 'components/ExportMenu/ExportMenuContainer';
import { WidgetT } from 'routes/Dashboard/components/WidgetsLayout/helpers';
import { ArgumentChartSelector } from '../ArgumentCharts/ArgumentChartSelector';
import { NestedMenuOptionT } from 'ui/NestedMenu/NestedMenu';
import ConditionnalZoomIconButton from 'components/ConditionnalZoomIconButton';
import { FULL_SCREEN_Z_INDEX } from 'contexts/FullScreenContext';
import NoDataState from 'ui/NoDataState/NoDataState';

const useHandleFullscreenInWidget = () => {
    const [initialCSSValues, setInitialCSSValues] = React.useState<{
        gridLayoutFilter: string;
        widgetTransform: string;
    }>({ gridLayoutFilter: '', widgetTransform: '' });
    const updateParentCSSOnFullscreen = (
        e: React.MouseEvent,
        isGoingFullscreen: boolean,
    ) => {
        const gridLayout = e.currentTarget.closest(
            '.react-grid-layout',
        ) as HTMLElement;
        const widgetEl = e.currentTarget.closest(
            '.react-grid-item',
        ) as HTMLElement;
        // if we go fullscreen, save the initial CSS values to restore them
        // when we exit fullscreen.
        if (!initialCSSValues.gridLayoutFilter) {
            setInitialCSSValues({
                gridLayoutFilter: gridLayout?.style.filter,
                widgetTransform: widgetEl?.style.transform,
            });
        }
        if (isGoingFullscreen) {
            gridLayout.style.filter = 'unset';
            widgetEl.style.transform = 'unset';
            widgetEl.style.zIndex = FULL_SCREEN_Z_INDEX.toString();
        } else {
            gridLayout.style.filter = initialCSSValues.gridLayoutFilter;
            widgetEl.style.transform = initialCSSValues.widgetTransform;
            widgetEl.style.zIndex = 'unset';
        }
    };
    return updateParentCSSOnFullscreen;
};

export type ArgumentChartWidgetProps = {
    editMode: boolean;
    widget: WidgetT;
    menuItems: NestedMenuOptionT[];
    isPdfModeActivated: boolean;
    lazyLoading: boolean;
};

export default function ArgumentChartWidget({
    editMode,
    widget,
    menuItems,
    isPdfModeActivated,
    lazyLoading,
}: ArgumentChartWidgetProps): React.ReactElement {
    const [exporterToExcel, setExporterToExcel] = React.useState<
        null | (() => () => void)
    >(null);
    // React Grid Layout applies a "transform" CSS property on the parent, which
    // breaks the fullscreen mode because "position: fixed" is relative to the nearest
    // parent with such a property (among other things). This hook disable the
    // CSS rules on the two offending parents. The user won't notice anything.
    const updateParentCSSOnFullscreen = useHandleFullscreenInWidget();
    const title = isPdfModeActivated ? (
        <WidgetTitle>
            <Typography variant="h5">{widget.chart?.title || ''}</Typography>
        </WidgetTitle>
    ) : (
        <WidgetTitle
            sx={{
                display: 'flex',
                alignItems: 'center',
                mr: '-8px',
            }}
        >
            <ConditionnalZoomIconButton onClick={updateParentCSSOnFullscreen} />
            <Typography variant="h5">{widget.chart?.title || ''}</Typography>
        </WidgetTitle>
    );
    if (!widget.chart) {
        return <NoDataState />;
    }
    return (
        <WidgetCard
            editMode={editMode}
            menuItems={menuItems}
            title={title}
            exportMenu={
                <ExportMenuContainer exportExcelData={exporterToExcel} />
            }
            lazyLoading={lazyLoading}
            sx={{ height: '100%' }}
        >
            <ArgumentChartSelector
                chartKey={widget.chart.key}
                setExporterToExcel={setExporterToExcel}
            />
        </WidgetCard>
    );
}
